import findKey from 'lodash/findKey';
import flatMap from 'lodash/flatMap';
import flowFP from 'lodash/fp/flow';
import groupByFP from 'lodash/fp/groupBy';
import mergeFP from 'lodash/fp/merge';
import orderByFP from 'lodash/fp/orderBy';
import partitionFP from 'lodash/fp/partition';
import map from 'lodash/map';
import prettyBytes from 'pretty-bytes';
import React from 'react';
import AssetIcon from '../../components/icon/common/AssetIcon';
import Icon from '../../components/icon/Icon';
import { getAddOnConfig } from '../../components/mapView/drawingManager/utilities';
import { IconWrapper } from '../../components/RightMenu/Product';
import { AMPLITUDE_EVENTS } from '../../constants/analytics';
import {
  BIM_FILE_EXT,
  FEATURE_PLAN_EXT,
  FILE_FORMAT,
} from '../../constants/asset';
import { ASSET_ICON_MAP, PRODUCT_TYPE } from '../../constants/product';
import text from '../../text';
import { amplitudeTrack } from '../../utilities';
import { client } from '../../utilities/api';
import { isAdministrator } from '../../utilities/user';
import { rtkApi } from '@/redux/rtk';
import classNames from 'classnames';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { Place2dPlanOnMapButton } from './Place2dPlanOnMapButton';

// Type definitions
interface Asset {
  id: string;
  filename: string;
  metadata: {
    size?: number;
    merged?: boolean;
  };
  product?: string;
  titleMessage?: string;
}

interface AssetIconWrapperProps {
  productType?: string;
  isSuperseded?: boolean;
}

interface ActivityAssetProps {
  projectId: string | number;
  assets: Asset[];
  onDelete?: (id: string) => void;
  currentUser?: {
    role: string;
  };
  isSuperseded?: boolean;
  isPaymentFlowUpload?: boolean;
}

interface Software {
  [key: string]: string[];
}

// Local-only: ASSET_ICON_MAP keys also populate the staff product picker.
const iconMap: Record<string, string> = mergeFP(ASSET_ICON_MAP, {
  Twod_Plan: '_2d_plan_on_map',
});

const SOFTWARE: Software = {
  Revit: ['rcs', 'rvt'],
  ArchiCAD: ['pln'],
  AutoCAD: ['dwg'],
  SketchUp: ['skp'],
  VectorWorks: ['vwx'],
  Rhino: ['3dm', 'ply'],
};

const getTitleMessage = (
  product: string,
  fileFormat: string,
  software?: string,
  tripodType: string = ''
): string => {
  switch (fileFormat) {
    case 'pointCloud3D': {
      const extraLabel = [
        PRODUCT_TYPE.INTERIOR,
        PRODUCT_TYPE.EXTERIOR,
      ].includes(product.toLowerCase())
        ? ' Tripod '
        : '';

      if (software) {
        return text('productPointCloud3DTitle', {
          product,
          software,
          extraLabel,
        });
      } else {
        return text('productGenericPointCloud3DTitle', {
          product,
          extraLabel,
        });
      }
    }
    case 'drawing2D':
      return `${text('drawing2DTitle', {
        tripod: tripodType,
        software,
      })}`;
    case 'bim3D':
      return `${text('bim3DTitle', {
        tripod: tripodType,
        software,
      })}`;
    case 'image':
      return text('image');
    case 'document':
      return text('document');
    case 'compressed':
      return text('compressed');
    default:
      return text('other');
  }
};

const getAssetType = (productType?: string): string =>
  productType ? iconMap[productType] : '';

const getFileExtension = (filename?: string): string | undefined =>
  filename ? filename.split('.').pop() : undefined;

const getFileGroupFromMap = (
  extension: string | undefined,
  groupMap: Record<string, string[]>
): string | undefined =>
  extension
    ? findKey(groupMap, (exts) => exts.includes(extension))
    : undefined;

const getExtensionFormat = (
  extension: string | undefined
): string | undefined => getFileGroupFromMap(extension, FILE_FORMAT);

const getExtensionSoftware = (
  extension: string | undefined
): string | undefined => getFileGroupFromMap(extension, SOFTWARE);

const getProductType = (filename: string): [string, string] => {
  const fileExtension = getFileExtension(filename);
  const regexTripod = /[-_.]?(?<product>Exterior|Interior)[-_.]/i;

  const regexBim =
    /[-_.]?(?<product>((Exterior|Interior)[-_.]?BIM|BIM[-_.]?(Exterior|Interior)))[-_.]/i;
  const matchBim = regexBim.exec(filename);
  if (matchBim && fileExtension && BIM_FILE_EXT.includes(fileExtension)) {
    const matchTripod = regexTripod.exec(filename);
    if (!matchTripod) {
      return ['', ''];
    }

    // returns BIM_<Exterior|Interior> at index 0 to set group and tag appropriate icon
    return [
      `BIM_${matchTripod.groups?.product}`,
      matchTripod.groups?.product || '',
    ];
  }

  // Anchored on token boundaries so street names ("Esplanade") don't match.
  const regexFeaturePlan = /(?:^|[-_. ])(?:Plan|2D[-_. ]?Feature)(?=[-_. ]|$)/i;
  if (
    regexFeaturePlan.test(filename) &&
    fileExtension &&
    FEATURE_PLAN_EXT.includes(fileExtension)
  ) {
    const matchTripod = regexTripod.exec(filename);
    if (!matchTripod) {
      // Only drawing2D gets the "2D Feature Plan" heading; a .pdf reads "Document".
      return getExtensionFormat(fileExtension) === 'drawing2D'
        ? ['Twod_Plan', '']
        : ['', ''];
    }

    // returns Twod_Plan_<Exterior|Interior> at index 0 to set group and tag appropriate icon
    return [
      `Twod_Plan_${matchTripod.groups?.product}`,
      matchTripod.groups?.product || '',
    ];
  }

  const regexPointClound =
    /[-_.]?(?<product>Drone|Exterior|Interior|Aerial|Streetscape)[-_.]/i;

  const matchToPointCloud = regexPointClound.exec(filename);
  if (matchToPointCloud) {
    return [matchToPointCloud.groups?.product || '', ''];
  }

  return ['', ''];
};

const setFileGrouping = (
  filename: string
): [[string, string], string | undefined] => [
  getProductType(filename),
  getFileExtension(filename),
];

// Product (bold) is the part before the "(for {software})" suffix; no paren = all product.
const splitProductTitle = (
  title = ''
): { product: string; software: string } => {
  const parenIndex = title.indexOf('(');
  if (parenIndex === -1) {
    return { product: title.trim(), software: '' };
  }
  return {
    product: title.slice(0, parenIndex).trim(),
    software: title.slice(parenIndex).trim(),
  };
};

const isPlaceableOnMap = (asset: Asset): boolean =>
  getExtensionFormat(getFileExtension(asset.filename)) === 'drawing2D';

const downloadFile = async (
  projectId: string | number,
  assetId: string
): Promise<void> => {
  const { data } = await client.getJobAsset(projectId, assetId);
  const link = document.createElement('a');
  link.download = '';
  link.href = data;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const AssetIconWrapper: React.FC<AssetIconWrapperProps> = (props) => {
  const productType = props?.productType || '';
  const isSuperseded = props?.isSuperseded || false;
  const isAddOn = !!getAddOnConfig(productType.toLowerCase());

  if (isAddOn) {
    return (
      <IconWrapper
        icon={productType.toLowerCase()}
        isActive={isSuperseded}
        isDisabled={false}
        isSecondary={false}
      />
    );
  }

  return (
    <AssetIcon
      product={getAssetType(productType)}
      classNames={classNames('overwrite-asset-icon-style', {
        superseded: isSuperseded,
      })}
      roundedCorners={false}
    />
  );
};

const ActivityAsset: React.FC<ActivityAssetProps> = (props) => {
  const {
    projectId,
    assets,
    onDelete,
    currentUser,
    isSuperseded,
    isPaymentFlowUpload = false,
  } = props;
  const { FILE_MANAGER } = AMPLITUDE_EVENTS;
  const dispatch = useAppDispatch();

  const handleClick = (assetId: string) => async (event: React.MouseEvent) => {
    event.preventDefault();
    amplitudeTrack(FILE_MANAGER.EVENT, {
      action: FILE_MANAGER.DOWNLOAD_INDIVIDUAL_FILES,
    });
    await downloadFile(projectId, assetId);
    dispatch(rtkApi.util.invalidateTags(['Project']));
  };

  const partitionGroups = flowFP([
    (result: Asset[]) =>
      map(result, (item) => {
        const [[product, tripodType], fileExtension] = setFileGrouping(
          item.filename
        );
        const fileFormat = getExtensionFormat(fileExtension);
        return {
          ...item,
          product,
          titleMessage: getTitleMessage(
            product,
            fileFormat || '',
            getExtensionSoftware(fileExtension),
            tripodType
          ),
        };
      }),
    partitionFP('product'), // Partition Larki assets and other assets, to prioritize larki products
  ])(assets);

  const titleGroups: { groupKey: string; groupAssets: Asset[] }[] = flatMap(
    partitionGroups,
    (group, partitionIndex) => {
      const groupedByTitle = flowFP([
        orderByFP('titleMessage', ['asc']),
        groupByFP('titleMessage'),
      ])(group);

      return map(groupedByTitle, (groupedAssets, titleMessage) => ({
        // A title can repeat across partitions, so it alone isn't a unique key.
        groupKey: `${partitionIndex}-${titleMessage}`,
        groupAssets: orderByFP('filename', ['asc'], groupedAssets) as Asset[],
      }));
    }
  );

  // Nothing separates the groups without product headings, so they read as one table.
  const renderedGroups = (
    isPaymentFlowUpload
      ? [
          {
            groupKey: 'uploadedFiles',
            groupAssets: orderByFP(
              'filename',
              ['asc'],
              flatMap(titleGroups, ({ groupAssets }) => groupAssets)
            ),
          },
        ]
      : titleGroups
  ).filter(({ groupAssets }) => groupAssets.length > 0);

  return map(renderedGroups, ({ groupKey, groupAssets }) => {
    const groupRep = groupAssets[0];
    const { product, software } = splitProductTitle(groupRep?.titleMessage);
    const placeableIds = new Set(
      groupAssets.filter(isPlaceableOnMap).map(({ id }) => id)
    );

    return (
      <div
        key={groupKey}
        className={classNames('ActivityAsset', {
          'mb-0': isPaymentFlowUpload,
        })}
      >
        {isPaymentFlowUpload ? null : (
          <div key={`${groupKey}-top`} className='Top align-items-center'>
            <div
              className={classNames('IconContainer mr-2', {
                superseded: isSuperseded,
              })}
            >
              <AssetIconWrapper
                productType={groupRep?.product}
                isSuperseded={isSuperseded}
              />
            </div>
            <span className='GroupHeading'>
              <strong>{product}</strong>
              {software ? ` ${software}` : ''} {text('filesLowercase')}
            </span>
          </div>
        )}
        <div key={`${groupKey}-bottom`} className='Bottom'>
          <div className='DownloadColumn'>
            <div className='ColumnHeading'>{text('download')}</div>
            {map(groupAssets, (item) => (
              <a
                className='Asset'
                key={item?.id}
                id={item?.id}
                onClick={handleClick(item?.id)}
                href='#'
              >
                <Icon
                  icon='download'
                  hasIconClass={false}
                  className='DownloadIcon'
                />
                <span className='Filename' title={item?.filename}>
                  {item?.filename}
                </span>
                <span className='FileSize'>
                  {prettyBytes(item?.metadata?.size || 1)}
                </span>
                {typeof item?.metadata.merged !== 'undefined' && (
                  <span className='Merged'>
                    ({item.metadata.merged ? 'Merged' : 'Unmerged'})
                  </span>
                )}
                {isAdministrator(currentUser?.role) && (
                  <div
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onDelete?.(item?.id);
                    }}
                  >
                    <Icon icon='cross' />
                  </div>
                )}
              </a>
            ))}
          </div>
          {projectId && placeableIds.size > 0 && (
            <div className='PlaceOnMapColumn'>
              <div className='ColumnHeading'>{text('placeOnMap')}</div>
              {/* A cell per file keeps each button on its filename's row. */}
              {map(groupAssets, (item) =>
                placeableIds.has(item.id) ? (
                  <Place2dPlanOnMapButton
                    key={item.id}
                    className='PlaceOnMapButton'
                    projectId={Number(projectId)}
                    assetId={item.id}
                  />
                ) : (
                  <div key={item.id} className='PlaceOnMapCell' />
                )
              )}
            </div>
          )}
        </div>
      </div>
    );
  });
};

export default ActivityAsset;
